import { combinadic, Combination, factoradic, Permutation, permutation, randomInteger } from "js-combinatorics"

/*

inputs:
- we are given an amount of days `N` that represents the number of days we have to travel
- we are given a weighted bidirectional graph presented in a form of square matrix `distances`,
	where `distances[i][j]` is the distance between `i` and `j` nodes
- we are given a weighted bidirectional graph presented in a form of matrix `times`,
	where `times[i][j]` is the distance between `i` and `j` nodes
- we are given an integer value `targetSum` that represents the total sum of all trips in the array
- we are given an integer value `dailyTimeLimit` that represents the maximum time we can travel in a single day
- we are given an integer value `timeSpentInNode` that represents the time we have to spend in each node

our goal is to fill the array with graph trips with the following restrictions:
- every array element is either null or a single graph trip in the form of array `[i,j,k,i]` with the visited nodes
- every graph trip in the array starts and ends in the root node
- every node visited in the graph trip has to be visited for `timeSpentInNode` time
- each graph trip cannot be longer than the `dailyTimeLimit`
- total sum of all trips in the array should be as close as possible to the `targetSum`, but not higher
- the array should contain as many different trips as possible
- the array should contain as many trips as possible
- each node should be visited at least once over the course of `N` days if possible

output:
- return an array of graph trips that satisfies the above conditions
- every graph trip should be in the form of array `[i,j,k,i]` with the visited nodes

*/


// calculate average distance per day
function averageDistancePerDay(N: number, targetSum: number, dailyTimeLimit: number): number
{
	let avgPerDay = targetSum / N
	if (avgPerDay > dailyTimeLimit)
		return dailyTimeLimit
	
	return avgPerDay
}

export function enrichData(
	trips: number[][],
	distances: number[][],
)
{
	let enriched = trips.map(t => ({
		trip: t.length ? [0, ...t, 0] : t,
		distance: pathDistance(distances, t),
	}))
	return {
		trips: enriched,
		totalDistance: enriched.reduce((sum, t) => sum + t.distance, 0),
	}
}
export function generateTripHistorySimple(
	N: number,
	distances: number[][],
	targetSum: number,
	dailyTripLimitMin: number,
	dailyTripLimitMax: number,
): number[][]
{
	validateInputsSimple(N, distances, targetSum, dailyTripLimitMin, dailyTripLimitMax)

	if (dailyTripLimitMax >= distances.length)
		dailyTripLimitMax = distances.length - 1

	let maxTrips = findNRandomPaths(N, distances, dailyTripLimitMax)
	let minTrips = maxTrips.map(p => p.slice(0, dailyTripLimitMin))
	let tripDistances = []
	let maxSum = 0
	let minSum = 0
	for (let i = 0; i < maxTrips.length; i++)
	{
		let max = pathDistance(distances, maxTrips[i]!)
		let min = pathDistance(distances, minTrips[i]!)
		tripDistances[i] = { max, min }
		maxSum += max
		minSum += min
	}

	// SCENARIO BREAKDOWN
	// now we have 3 scenarios:
	// 1. not enough trips to reach the target sum
	// 2. target sum is somewhere between (min, max) and we need to reach it
	// 3. target sum is so small that even the minimum trips are too much

	// SCENARIO 1
	// if the total sum of all trips is less than the target sum, we are unable to reach the target sum
	if (maxSum < targetSum)
	{
		// so we will do our best with the longest trips possible
		// generate several other random paths and check again
		for (let i = 0; i < 5; i++)
		{
			let trips = findNRandomPaths(N, distances, dailyTripLimitMax)
			let sum = trips.reduce((sum, trip) => sum + pathDistance(distances, trip), 0)
			if (sum > maxSum)
			{
				maxTrips = trips
				maxSum = sum
			}
		}
	}
	// hopefully we have enough trips to reach the target sum,
	// but if not, let's just give up and return whatever we have
	if (maxSum < targetSum)
		return maxTrips

	// SCENARIO 3
	// target sum is so small that even the minimum trips are too much
	// we need to minimize the total sum, so we will skip some days and use shorter trips if needed
	if (minSum > targetSum)
		return randomizeArrayToLength(SCENARIO_3(targetSum, minTrips, maxTrips, tripDistances), N)

	// SCENARIO 2
	// target sum is somewhere between (min, max) and we need to reach it
	return SCENARIO_2(targetSum, minTrips, maxTrips, tripDistances)
}
function SCENARIO_2(
	targetSum: number,
	minTrips: number[][],
	maxTrips: number[][],
	tripDistances: {max: number, min: number}[],
)
{
	// we can do a binary search to find the best combination of trips
	// we will use the following strategy:
	// - we take half of the minimum trips and half of the maximum trips (stored in a length diff array)
	// - we calculate the total sum of all trips
	// - if the total sum is less than the target sum, we increase the minimum trips
	// - if the total sum is greater than the target sum, we decrease the maximum trips
	// - we repeat the process until we reach the target sum or until we reach the maximum number of iterations
	let split = binarySearchForSplit(targetSum, tripDistances)
	let arr = returnWithSplit(minTrips, maxTrips, split)
	// then we can cleanup part of the trips that are longer than expected
	return shuffleArray(arr)
}
function binarySearchForSplit(
	targetSum: number,
	tripDistances: {max: number, min: number}[]
)
{
	let left = 0
	let right = tripDistances.length - 1
	while (left <= right)
	{
		let mid = Math.floor((left + right) / 2)
		let sum = sumAtSplit(tripDistances, mid)
		if (sum > targetSum)
			right = mid - 1
		else if (sum < targetSum)
			left = mid + 1
		else
			return mid
	}
	return left
}
function shuffleArray<T>(arr: T[]): T[]
{
	return new Combination(arr, arr.length).sample()!
}
function returnWithSplit<T>(left: T[], right: T[], split: number): T[]
{
	return left.slice(0, split).concat(right.slice(split))
}
function sumAtSplit(
	tripDistances: {max: number, min: number}[],
	split: number
)
{
	return tripDistances.reduce((sum, d, i) => sum + ((i < split) ? d.min : d.max), 0)
}
function SCENARIO_3(
	targetSum: number,
	minTrips: number[][],
	maxTrips: number[][],
	tripDistances: {max: number, min: number}[],
)
{
	// target sum is so small that even the minimum trips are too much
	// we need to minimize the total sum, so we will skip some days and use shorter trips if needed

	// we start with a zero sum and carefully add trips until we reach the target sum
	let sum = 0
	let trips: number[][] = []
	for (let i = 0; i < minTrips.length; i++)
	{
		let s = calcMinMaxHalfChances(i, minTrips.length)
		let trip = selectMinMaxHalf(minTrips[i]!, maxTrips[i]!, s)
		let distance = selectDistance(tripDistances[i]!.min, tripDistances[i]!.max, s)
		if (sum + distance > targetSum)
		{
			// we got to the end
			trips.push(minTrips[i]!)
			break
		}
		trips.push(trip)
		sum += distance
	}
	return trips
}
function randomizeArrayToLength(arr: number[][], len: number): number[][]
{
	let result = arr.slice()
	while (result.length < len)
	{
		let i = Math.floor(Math.random() * result.length)
		result.splice(i, 0, [])
	}
	return result
}
function selectMinMaxHalf(min: number[], max: number[], s: "min" | "max"): number[]
{
	if (s === "min")
		return min
	if (s === "max")
		return max
	
	return randomInteger(0, 1) === 0 ? min : max
}
function selectDistance(min: number, max: number, s: "min" | "max"): number
{
	if (s == "min")
		return min
	
	return max
}
function calcMinMaxHalfChances(len: number, total: number): "min" | "max"
{
	if (len < 5)
		return "min"

	let chanceHalf = 0
	if (len > 0)
		chanceHalf = (len / 3) / total

	let chanceMax = 0
	if (len > (total / 2))
		chanceMax = ((len - (total / 2)) / 10) / len

	let rnd = Math.random()
	if (rnd < chanceMax)
		return "max"
	// if (rnd < chanceHalf + chanceMax)
	// 	return "half"
	return "min"
}
export function pathDistance(distances: number[][], path: number[])
{
	if (!path.length)
		return 0

	let sum = 0
	let from = 0
	// console.log(path, sum)
	for (let i = 0; i < path.length; i++)
	{
		let to = path[i]!
		let distance = distances[from]![to]!
		sum += distance
		// console.log(`${i}: added ${distance} to sum (${from}->${to}), sum is now ${sum}`)
		from = to
	}
	if (from != 0)
		sum += distances[from]![0]!
	
	// console.log(`final sum is ${sum}`)

	return sum
}
export function permsOf(n: number, k: number)
{
	let arr = new Array(n).fill(0).map((_, i) => i)
	let p = new Permutation(arr, k)
	return p
}
export function findNRandomPaths(
	N: number,
	distances: number[][],
	dailyTripLimitMax: number,
)
{
	let p = permsOf(distances.length - 1, dailyTripLimitMax)
	// let total = totalPathsAmount(distances.length, dailyTripLimitMax)
	// let total = permutation(distances.length - 1, dailyTripLimitMax)
	// let ca = combinadic(dailyTripLimitMax, distances.length - 1)
	// for each value, generate a path
	let paths: number[][] = []
	while (paths.length < N)
	{
		let path = p.sample()
		if (!path)
			continue
		paths.push(path.map(p => p + 1))
	}
	// return the paths
	return paths
}
export function idToPath(lehmerCode: bigint, n: number, pathLen: number, total: number): number[]
{
	// calculate the path from the lehmerCode
	// the id is a number that represents the path
	// lehmerCode goes from {0} to {total - 1}
	// the path is a list of nodes
	// example:
	//   n = 3, pathLen = 2, total: 2
	//   lehmerCode = 0
	//   path = [1, 2]
	//   lehmerCode = 1
	//   path = [2, 1]
	// another example:
	//   n = 4, pathLen = 3, total: 6
	//   lehmerCode = 0
	//   path = [1, 2, 3]
	//   lehmerCode = 1
	//   path = [1, 3, 2]
	//   lehmerCode = 2
	//   path = [2, 1, 3]
	//   lehmerCode = 3
	//   path = [2, 3, 1]
	//   lehmerCode = 4
	//   path = [3, 1, 2]
	//   lehmerCode = 5
	//   path = [3, 2, 1]
	// another example:
	//   n = 4, pathLen = 1, total: 6
	//   lehmerCode = 0
	//   path = [1]
	//   lehmerCode = 1
	//   path = [2]
	//   lehmerCode = 2
	//   path = [3]
	
	let path: number[] = []
	while (pathLen > 0)
	{
		let div = lehmerCode / BigInt(n)
		let mod = lehmerCode % BigInt(n)
		path.push(Number(mod))
		lehmerCode = div
		pathLen--
	}
	return path
}
export function totalPathsAmount(
	n: number,
	pathLength: number
)
{
	// impossible to generate enough paths
	if (pathLength > (n - 1))
		return 0n

	// incorrect input
	if (n == 0)
		return 0n
	// no targets to travel to
	if (n == 1)
		return 0n
	// no path length
	if (pathLength == 0)
		return 0n

	if (n == 2)
		return 1n
	if (pathLength == 1)
		return BigInt(n - 1)

	// initial paths: n - 1
	// next paths: recursive
	let total = 1n
	let options = n - 1
	while (pathLength > 0)
	{
		total *= BigInt(options)
		options--
		pathLength--
	}
	return total
}
export function calculateSortedNodes(distances: number[][])
{
	// calculate the sorted nodes from each node
	let sortedMax = new Map<number, number[]>()
	for (let i = 0; i < distances.length; i++)
	{
		let sorted = distances[i]!.map((d, j) => ({ node: j, distance: d }))
			.filter(p => p.distance > 0)
			.sort((a, b) => b.distance - a.distance)
			.map(p => p.node)
		sortedMax.set(i, sorted)
	}
	return sortedMax
}
// export function findNLongestPaths(
// 	N: number,
// 	distances: number[][],
// 	dailyTripLimitMax: number,
// ): number[][]
// {
// 	type Path = { path: number[], distance: number }
// 	let paths: Path[] = []
	
// 	let sorted = calculateSortedNodes(distances)

// 	// calculate the longest paths

// 	let n = distances.length


// 	paths.sort((a, b) => b.distance - a.distance)
// 	return paths.map(p => p.path)
// }

export function generateTripHistory(
	N: number,
	distances: number[][],
	times: number[][],
	targetSum: number,
	dailyTimeLimit: number,
	timeSpentInNode: number
): number[][]
{
	throw new Error("Not implemented")
	validateInputs(N, distances, times, targetSum, dailyTimeLimit, timeSpentInNode)

	// we will calculate the total capacity over N days
	// and use different strategies for the following cases:
	// 1. if the total capacity is less than the target sum, we are unable to reach the target sum,
	//    so we will do our best with the longest trips possible
	// 2. if the total capacity is greater than the target sum, but not too much, we will try to reach the target sum
	//    by using a smart algorithm
	// 3. if the total capacity is much greater than the target sum (e.g. 10x), our goal is reverse:
	//    we need to minimize the total sum, so we will use shorter daily trip counts and shorter trip times
	//
	// the desired goals are just two:
	// - make as many trips as possible
	// - spread the trips thinly over the course of N days
	// 
	// we can spread trips thinly by using the following strategy:
	// - calculate 
	
	let n = distances.length
	let trips: number[][] = []
	let visited = new Set<number>()
	let totalSum = 0
	let days = 0
	let current = 0

	// calculate min and max distance possible to drive in a single trip
	let min = Infinity
	let maxDistPerTime = 0
	for (let i = 0; i < n; i++)
	{
		for (let j = 0; j < n; j++)
		{
			if (i === j)
				continue
			
			if (distances[i]![j] == 0)
				throw new Error(`Invalid distance between nodes ${i} and ${j}: ${distances[i]![j]}`)
			if (times[i]![j] == 0)
				throw new Error(`Invalid time between nodes ${i} and ${j}: ${times[i]![j]}`)

			min = Math.min(min, distances[i]![j]! * 2) // multiply by 2 because it's a round trip
			let distPerTime = distances[i]![j]! / times[i]![j]!
			if (distPerTime > maxDistPerTime)
				maxDistPerTime = distPerTime
		}
	}
	let maxDistPerDay = dailyTimeLimit * maxDistPerTime
	while (days < N)
	{
		let daySum = 0
		let trip: number[] = []
		let time = 0

		while (time < dailyTimeLimit)
		{
			let min = Infinity
			let next = -1

			for (let i = 0; i < n; i++)
			{
				if (i === current || visited.has(i))
					continue

				if (distances[current]![i]! < min)
				{
					min = distances[current]![i]!
					next = i
				}
			}

			if (next === -1)
				break

			let tripTime = times[current]![next]! + timeSpentInNode
			if (time + tripTime > dailyTimeLimit)
				break

			trip.push(current, next, tripTime, current)
			daySum += distances[current]![next]!
			time += tripTime
			current = next
			visited.add(next)
		}

		if (trip.length === 0)
			break

		trips.push(trip)
		totalSum += daySum
		days++
	}
}

export function validateInputsSimple(
	N: number,
	distances: number[][],
	targetSum: number,
	dailyTripLimitMin: number,
	dailyTripLimitMax: number,
)
{
	if (N <= 0)
		throw new Error(`Invalid number of days: ${N}`)

	let n = distances.length
	if (n < 2)
		throw new Error(`Invalid number of nodes: ${n}`)

	for (let i = 0; i < n; i++)
	{
		if (distances[i]!.length !== n)
			throw new Error(`distances matrix is not square (row ${i})`)

		if (distances[i]![i] !== 0)
			throw new Error(`distances[${i}][${i}] should be 0, instead got ${distances[i]![i]}`)
	}

	if (targetSum <= 0)
		throw new Error(`Invalid target sum: ${targetSum}`)
	if (dailyTripLimitMin < 0)
		throw new Error(`Invalid daily time limit: ${dailyTripLimitMin}`)
	if (dailyTripLimitMax < dailyTripLimitMin)
		throw new Error(`Invalid daily time limit: ${dailyTripLimitMax} < ${dailyTripLimitMin}`)
}

export function validateInputs(
	N: number,
	distances: number[][],
	times: number[][],
	targetSum: number,
	dailyTimeLimit: number,
	timeSpentInNode: number
)
{
	if (N <= 0)
		throw new Error(`Invalid number of days: ${N}`)

	let n = distances.length
	if (n < 2)
		throw new Error(`Invalid number of nodes: ${n}`)

	for (let i = 0; i < n; i++)
	{
		if (distances[i]!.length !== n)
			throw new Error(`distances matrix is not square (row ${i})`)
		if (times[i]!.length !== n)
			throw new Error(`times matrix is not square (row ${i})`)

		if (distances[i]![i] !== 0)
			throw new Error(`distances[${i}][${i}] should be 0, instead got ${distances[i]![i]}`)
		if (times[i]![i] !== 0)
			throw new Error(`times[${i}][${i}] should be 0, instead got ${times[i]![i]}`)
	}

	if (targetSum <= 0)
		throw new Error(`Invalid target sum: ${targetSum}`)
	if (dailyTimeLimit <= 0)
		throw new Error(`Invalid daily time limit: ${dailyTimeLimit}`)
	if (timeSpentInNode < 0)
		throw new Error(`Invalid time spent in node: ${timeSpentInNode}`)
}
